import PropTypes from 'prop-types';
import React, { useState } from 'react';
import './terms-acceptance.css';

// Ties the label to the box it ticks. One checkout draws one terms line, so a
// fixed id is enough and keeps the association readable.
const TERMS_FIELD_ID = 'checkout-terms-accepted';

// The default first half of the agree line. core-admin applies the same default
// when it resolves the element, and ordergate's tnc-service again on the way
// through, so this is the last of three and covers only a `tnc` that reached
// this component from somewhere older than the split.
const DEFAULT_STATEMENT_START = 'I agree to the ';

// The same words wherever this line is drawn - core-admin's form renderer, the
// admin preview, here. It is the same affordance, so it says the same thing.
const DETAILED_TERMS_HINT = 'Click to view full terms';

const VIEW_MODE = {
  COMPACT: 'compact',
  SUMMARY: 'summary',
  FULL: 'full',
};

// What goes on the page above the tick, for terms written out rather than kept
// in a document. This is the whole of what the view mode decides: compact
// prints neither body, summary prints the short one, full prints the whole
// thing. Each arm falls through to the other body, so an element authored with
// only a fullHtml still says something in summary mode.
function resolveInlineHtml({ viewMode, summaryHtml, fullHtml }) {
  if (viewMode === VIEW_MODE.COMPACT) return '';
  if (viewMode === VIEW_MODE.FULL) return fullHtml || summaryHtml || '';

  return summaryHtml || fullHtml || '';
}

// What opens behind the agree line's link - only ever terms the buyer has not
// already been given. In full mode the whole thing is already above the tick,
// so this is empty and the name stops being a link.
function resolveDetailedHtml({ viewMode, summaryHtml, fullHtml }) {
  const inlineHtml = resolveInlineHtml({ viewMode, summaryHtml, fullHtml });
  const detailedHtml = fullHtml || summaryHtml || '';

  return detailedHtml && detailedHtml !== inlineHtml ? detailedHtml : '';
}

/**
 * The terms at checkout: as much of them as the view mode prints, then the tick
 * that has to be given before the order can be taken any further.
 *
 * The line beside the tick is two authored halves - the statement that opens
 * it, then the name of the terms, which is the only clickable part. What that
 * click opens is whatever the buyer has not already been shown: a document
 * opens in a new tab, terms written out open in a modal, and terms already
 * printed in full open nothing, because there is nothing left to open.
 *
 * `tnc` is the element core-admin resolved for this order's delivery mode, or
 * null when there isn't one - an org that has never authored it, an admin who
 * switched it off, a core-admin that could not be reached. Null is an ordinary
 * case, not an error: the component then draws exactly the line this page drew
 * before any of this existed, a tick beside a link to the published terms, so a
 * terms service being down never stops somebody paying.
 *
 * The tick itself is the same react-hook-form field either way, so the schema
 * that makes it mandatory does not have to know which of the two was drawn.
 */
function TermsAcceptance({
  tnc, register, error, fallbackHref,
}) {
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  // Bound field by field rather than spread, so the checkbox stays a plain
  // element the linter can see the props of. Same registration either way.
  const termsField = register('terms');

  // ?? not ||: an author who emptied the field meant the empty string.
  const statementStart = tnc?.statementStart ?? DEFAULT_STATEMENT_START;
  const linkText = tnc?.title || 'terms & conditions';
  const isFileBacked = Boolean(tnc?.fileUrl);
  const inlineHtml = tnc
    ? resolveInlineHtml({
      viewMode: tnc.viewMode,
      summaryHtml: tnc.summaryHtml,
      fullHtml: tnc.fullHtml,
    })
    : '';
  // A document is always worth opening, whatever is printed above it - the file
  // is the terms, and no amount of summary stands in for it.
  const modalHtml = !tnc || isFileBacked
    ? ''
    : resolveDetailedHtml({
      viewMode: tnc.viewMode,
      summaryHtml: tnc.summaryHtml,
      fullHtml: tnc.fullHtml,
    });

  const linkClass = 'text-blue-600 font-medium hover:underline';

  // Three shapes of the clickable half, in the order they are decided: no
  // element at all falls back to the published terms page this checkout linked
  // to before any of this existed; a document opens as a link; terms written out
  // open in the modal. Terms with nothing left behind them - already printed in
  // full, or never written - are named but not offered as a link, because a
  // dead click reads as broken.
  const detailedTermsLink = () => {
    if (!tnc) {
      return (
        <a target="_blank" href={fallbackHref} className={linkClass} rel="noreferrer">
          terms &amp; conditions
        </a>
      );
    }

    if (isFileBacked) {
      return (
        <a
          target="_blank"
          href={tnc.fileUrl}
          title={DETAILED_TERMS_HINT}
          className={linkClass}
          rel="noreferrer"
        >
          {linkText}
        </a>
      );
    }

    if (!modalHtml) return <span className="font-medium">{linkText}</span>;

    return (
      <button
        type="button"
        onClick={() => setIsTermsOpen(true)}
        title={DETAILED_TERMS_HINT}
        className={linkClass}
      >
        {linkText}
      </button>
    );
  };

  return (
    <>
      {inlineHtml && (
        <div
          className="tnc-body w-[90%] lg:w-4/5 mb-3"
          // Admin-authored rich text out of core-admin's json element registry,
          // the same trust boundary the back office renders question content on.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: inlineHtml }}
        />
      )}

      <label
        htmlFor={TERMS_FIELD_ID}
        className="w-[90%] lg:w-4/5 flex items-center space-x-2 cursor-pointer"
      >
        <input
          id={TERMS_FIELD_ID}
          type="checkbox"
          name={termsField.name}
          ref={termsField.ref}
          onChange={termsField.onChange}
          onBlur={termsField.onBlur}
          className="w-5 h-5 container"
        />
        <span className="text-gray-700">
          {statementStart}
          {detailedTermsLink()}
        </span>
      </label>

      {isTermsOpen && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-4">
          <div
            className="bg-white w-full lg:w-[640px] rounded-t-2xl lg:rounded-xl overflow-hidden flex flex-col max-h-[90vh]"
            role="dialog"
            aria-modal="true"
            aria-label={linkText}
          >
            <div className="px-5 py-3 flex items-start justify-between border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-800 pr-4">{linkText}</h3>
              <button
                type="button"
                onClick={() => setIsTermsOpen(false)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <div
              className="tnc-body px-5 py-4 overflow-y-auto"
              // Same admin-authored rich text, same trust boundary as the body
              // printed above the tick.
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: modalHtml }}
            />
          </div>
        </div>
      )}

      <div className="w-4/5 mb-8 text-sm flex items-center space-x-2 text-red-500">
        {error && (
          <>
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-300 text-white text-sm font-bold">
              !
            </span>
            <span>{error}</span>
          </>
        )}
      </div>
    </>
  );
}

export default TermsAcceptance;

TermsAcceptance.propTypes = {
  tnc: PropTypes.shape({
    elementName: PropTypes.string,
    viewMode: PropTypes.string,
    // The agree line's two halves: the statement that opens it, and the terms'
    // name, which is the clickable one.
    statementStart: PropTypes.string,
    title: PropTypes.string,
    summaryHtml: PropTypes.string,
    fullHtml: PropTypes.string,
    fileUrl: PropTypes.string,
    fileTitle: PropTypes.string,
  }),
  register: PropTypes.func.isRequired,
  error: PropTypes.string,
  fallbackHref: PropTypes.string.isRequired,
};
